import { useCallback, useDeferredValue, useMemo, useState } from "react";

import type {
    CompanyAnalysisTechnologyItem,
    CompanyAnalysisTechnologyOption,
} from "app/pages/sales-intelligence/modules/technology/types/company-analysis-technology-filter";
import { is } from "app/pages/sales-intelligence/utils/type-guards";
import {
    enrichWithStatusAndPeriods,
    filterByStatus,
    getSortedSubdomainCounts,
} from "app/pages/website-analysis/website-technologies/technologyList";
import { StatusFilterValue } from "app/pages/website-analysis/website-technologies/types";

export const useFilteredTechnologies = (
    technologies: CompanyAnalysisTechnologyItem[] | undefined,
    isFeatureInHookMode = false,
) => {
    const enrichedTechnologies = useMemo(
        () => (technologies ? enrichWithStatusAndPeriods(technologies) : []),
        [technologies],
    );
    const subdomainCounts = useMemo(() => getSortedSubdomainCounts(enrichedTechnologies), [enrichedTechnologies]);

    const getInitiallySelectedSubdomains = () => {
        if (isFeatureInHookMode) {
            return [];
        }

        return Object.keys(subdomainCounts) || [];
    };

    const [appliedCategories, setAppliedCategories] = useState<string[]>([]);
    const [searchString, setSearchString] = useState("");
    const [statusFilterValue, setStatusFilterValue] = useState(StatusFilterValue.InstalledAndRemoved);
    const [currentPage, setCurrentPage] = useState(1);

    const [selectedSubdomains, setSelectedSubdomains] = useState<string[]>(getInitiallySelectedSubdomains());

    const handleApplySelectedTechCategories = useCallback((categories: CompanyAnalysisTechnologyOption[]) => {
        setAppliedCategories(categories.map((item) => item.text));
    }, []);

    const handleSearch = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchString(e.target.value || "");
    }, []);

    const filterByCategory = useCallback(
        (items: typeof enrichedTechnologies) =>
            appliedCategories.length ? items.filter((tech) => appliedCategories.includes(tech.subCategory)) : items,
        [appliedCategories],
    );

    const deferredSearchString = useDeferredValue(searchString);

    const filterBySearch = useCallback(
        (items: typeof enrichedTechnologies) => {
            const queryWords = deferredSearchString.trim().toLowerCase().split(/\s+/);
            if (!queryWords.length) {
                return items;
            }
            return items.filter((item) =>
                Object.values(item).some((val) => {
                    if (is.string(val) || is.number(val) || is.boolean(val)) {
                        return queryWords.some((word) => val.toString().toLowerCase().includes(word));
                    }
                    return false;
                }),
            );
        },
        [deferredSearchString],
    );

    const filterBySubDomain = useCallback(
        (items: typeof enrichedTechnologies) => {
            if (!selectedSubdomains.length) {
                return items;
            }

            const filtered = items.reduce<CompanyAnalysisTechnologyItem[]>((acc, item) => {
                const filteredUsages = item.usages.filter((usage) => {
                    return selectedSubdomains.includes(usage.site);
                });

                if (filteredUsages.length > 0) {
                    acc.push({ ...item, usages: filteredUsages });
                }

                return acc;
            }, []);

            return enrichWithStatusAndPeriods(filtered);
        },
        [selectedSubdomains],
    );

    const filteredTechnologies = useMemo(() => {
        const filters = [
            filterByCategory,
            filterBySearch,
            filterBySubDomain,
            (items: typeof enrichedTechnologies) => filterByStatus(statusFilterValue)(items),
        ];
        return filters.reduce((acc, filter) => filter(acc), enrichedTechnologies);
    }, [enrichedTechnologies, filterByCategory, filterBySearch, filterBySubDomain, statusFilterValue]);

    return {
        appliedCategories,
        searchString,
        statusFilterValue,
        currentPage,
        handleApplySelectedTechCategories,
        handleSearch,
        setStatusFilterValue,
        filteredTechnologies,
        setCurrentPage,
        isSubDomainFilterDisabled: Object.keys(subdomainCounts).length < 1,
        selectedSubdomains,
        setSelectedSubdomains,
        subdomainCounts,
        getInitiallySelectedSubdomains,
    };
};
